import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, timer } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { ISettings } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { KeepAliveService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ExtendedUserIdleService } from './extended-user-idle.service';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

/**
 * Service for showing the action sheet.
 */
@Injectable({ providedIn: 'root' })
export class IdleService {
    public countdownStream: Observable<number>;
    public countdownStartedStream: Observable<boolean>;
    public countdownFinishedStream: Observable<boolean>;
    public isActiveStream: Observable<boolean>;
    private _isIdleTimerFlaggedOff: boolean;
    private _hasIdleTimerInSettings: boolean;
    private _routeUrlBlacklist?: string[] = [];
    private _featureFlagSubscriptions = new Map<string, Subscription>();

    // Time between keepAlive calls in seconds.
    protected keepAlivePingTimeSec = 60 * 2;

    private activitySinceLastPing = false;

    private _timerSub: Subscription;
    private _activitySub: Subscription;

    constructor(
        @Inject(SETTINGS) private _settings: ISettings,
        private _featureFlagService: FeatureFlagService,
        private _keepAliveService: KeepAliveService,
        private _userService: UserService,
        private _router: Router,
        private _userIdleService: ExtendedUserIdleService
    ) {
        SingletonServiceGuard.register('IdleService');
        this._hasIdleTimerInSettings =
            this._settings.idleTimerInSeconds &&
            this._settings.idleTimerInSeconds > 0 &&
            this._settings.idleModalLifespanInSeconds &&
            this._settings.idleModalLifespanInSeconds > 0;

        this.countdownStream = this._userIdleService
            .onTimerStart()
            .pipe(
                filter(
                    (secondsElapsed) =>
                        this._isActive && typeof secondsElapsed === 'number' && secondsElapsed >= 1
                )
            );

        this.countdownStartedStream = this.countdownStream.pipe(
            filter((secondsElapsed) => secondsElapsed === 1),
            map(() => true)
        );

        this.countdownFinishedStream = this._userIdleService
            .onTimeout()
            .pipe(filter(() => this._isActive));

        if (this._hasIdleTimerInSettings) {
            this._userIdleService.setConfigValues({
                idle: this._settings.idleTimerInSeconds,
                timeout: this._settings.idleModalLifespanInSeconds,
                ping: this.keepAlivePingTimeSec
            });
        }
    }

    private get _isActive(): boolean {
        return (
            !this._isIdleTimerFlaggedOff &&
            this._hasIdleTimerInSettings &&
            this._routeUrlBlacklist.every(
                (blacklistedPattern) =>
                    !this._router.url.match(new RegExp(blacklistedPattern, 'gi'))
            )
        );
    }

    public start(): void {
        this._userIdleService.startWatching();
        if (!this._timerSub) {
            this._timerSub = timer(
                this.keepAlivePingTimeSec * 1000,
                this.keepAlivePingTimeSec * 1000
            )
                .pipe(
                    filter(() => this._isActive),
                    withLatestFrom(this._userService.user$)
                )
                .subscribe(async ([timer, user]) => {
                    if (user && this.activitySinceLastPing) {
                        this._keepAliveService.executeOnce();
                        this.activitySinceLastPing = false;
                    }
                });
        }
        if (!this._activitySub) {
            this._activitySub = this._userIdleService.activityEvents$
                .pipe(filter(() => !this.activitySinceLastPing))
                .subscribe((event) => {
                    this.activitySinceLastPing = true;
                });
        }
    }

    public addFeatureFlag(featureFlagKey: string): void {
        if (!this._featureFlagSubscriptions.has(featureFlagKey)) {
            this._featureFlagSubscriptions.set(
                featureFlagKey,
                this._featureFlagService.isOn(featureFlagKey).subscribe((featureFlagIsOn) => {
                    this._isIdleTimerFlaggedOff = !featureFlagIsOn;
                    if (this._isIdleTimerFlaggedOff) {
                        this.stop();
                    }
                })
            );
        } else if (!this._settings.production) {
            console.warn('[IdleService#addFeatureFlag] Tried to add the same flag more than once.');
        }
    }

    public removeFeatureFlag(featureFlagKey: string): void {
        const subscription = this._featureFlagSubscriptions.get(featureFlagKey);
        if (!!subscription) {
            subscription.unsubscribe();
        }
        this._featureFlagSubscriptions.delete(featureFlagKey);
    }

    public setRouteUrlBlacklist(blacklist: string[]): void {
        this._routeUrlBlacklist = blacklist;
    }

    public reset(): void {
        this._userIdleService.resetTimer();
    }

    public stop(): void {
        this._userIdleService.stopTimer();
        this._userIdleService.stopWatching();
    }
}
